import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Virtuoso } from "react-virtuoso";

import {
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaEllipsisH,
  FaTrash,
  FaPen,
  FaArrowLeft,
  FaRegFolder,
  FaRegFolderOpen
} from "react-icons/fa";
import { GrUpgrade } from "react-icons/gr";
import { IoMdShareAlt } from "react-icons/io";
import { BsFolderPlus, BsFolder } from "react-icons/bs";
import { BiEdit } from "react-icons/bi";
import {
  RiUserLine,
  RiSettings4Line,
  RiBillLine,
  RiLogoutBoxRLine,
  RiEditBoxLine
} from "react-icons/ri";
import { CiGrid41, CiServer, CiMoneyCheck1, } from "react-icons/ci";
import { FaPlusCircle } from "react-icons/fa";
import { PiReceiptLight } from "react-icons/pi";
import { MdLogout, MdLogin } from "react-icons/md";
import { FaXRay } from "react-icons/fa";
import { BsFiles } from "react-icons/bs";
import { PanelLeftIcon, Users } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { TbApps } from "react-icons/tb";

import logo from '../../assets/images/logo.png';
import userImage from '../../assets/icons/user-image.png';
import ProfileAvatar from "@minohealth/common/components/profile-avatar";
import useSwitchAccounts from "@minohealth/common/hooks/useSwitchAccounts";
import { removeCookie, showConfirm, showErrorMessage } from "@minohealth/common/utils";
import { setUser } from "@minohealth/common/services/DataStoreService/reducers/AppReducer";
import AppNavigationContext from "@minohealth/common/services/AppNavigationContext";

// ProjectSelect removed from sidebar UI per design — projects hidden in collapsed state
import useOnClickOutside from "@minohealth/common/hooks/useOnClickOutside";
import { ChatSessionSharingWindow } from "../anokye-content/components/anokye-sharing-window";
import { useChatContext } from "../../services/chat-provider";
import NewProjectModal from "./extras/NewProjectModal";
import AppsList from "./extras/AppsList";
import ThemeToggle from "./extras/ThemeToggle";
import { FeatureFlags } from "@minohealth/common/lib/feature-flags";
import ProfileMenuCustom from "../profile-menu/ProfileMenuCustom";

// --- Components ---

const NewChatButton = ({ onClick }) => {
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const { user } = useSelector((state) => state.app.datastate);

  const handleClick = async () => {
    // If user is not authenticated, prompt them to login/signup first
    if (!user?._id) {
      showConfirm(
        "You need to sign in to start a new chat.",
        "Authentication required",
        ""
      )
        .then(() => history.push("/login"))
        .catch(() => { });
      return;
    }

    const urlArray = window.location.pathname.split("/");
    if (urlArray[urlArray.length - 1] !== "chat") {
      history.push("/");
    }

    setIsLoading(true);
    try {
      await onClick();
    } catch (error) {
      console.error("Failed to create new chat:", error);
      Sentry.captureException(error, { extra: { context: 'NewChatButton' } });
      showErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className="w-full flex items-center gap-2 px-2 py-2 text-sm text-foreground bg-transparent hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
      title="Untitled chat"
    >
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" />
      ) : (
        <RiEditBoxLine className="w-5 h-5 text-muted-foreground transition-transform duration-200 group-hover:scale-110" />
      )}
      <span className="font-medium">New chat</span>
    </button>
  );
};

const NewProjectButton = ({ onClick }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.app.datastate);
  const history = useHistory();

  const handleClick = async () => {
    if (!user?._id) {
      showConfirm(
        "You need to sign in to create a new project.",
        "Authentication required",
        ""
      )
        .then(() => history.push("/login"))
        .catch(() => { });
      return;
    }

    setIsLoading(true);
    try {
      await onClick();
    } catch (error) {
      console.error("Failed to create new project:", error);
      showErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className="w-full flex items-center gap-2 px-2 h-9 text-sm text-foreground bg-transparent hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
      title="New project"
    >
      <div className="flex-shrink-0">
        {isLoading ? (
          <div className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <BsFolderPlus className="w-4 h-4" />
        )}
      </div>
      <span className="font-medium truncate">New project</span>
    </button>
  );
};

const HistoryItem = ({ label, isActive, isShared, onClick, onShare, onDelete }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useOnClickOutside(menuRef, () => setIsMenuOpen(false));

  return (
    <div
      className={`group flex items-center justify-between px-2 h-9 text-xs rounded-lg cursor-pointer transition-colors border ${isActive
        ? "border-green-800 bg-green-900/20 text-foreground"
        : "border-transparent text-muted-foreground hover:bg-muted"
        }`}
      onClick={onClick}
    >
      <span className="truncate flex-1">{label}</span>
      {isShared && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0 w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center mr-1">
              <Users className="w-2.5 h-2.5 text-blue-400" />
            </span>
          </TooltipTrigger>
          <TooltipContent>Shared with you</TooltipContent>
        </Tooltip>
      )}
      <div className="relative" ref={menuRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
          className={`p-1.5 rounded-md bg-transparent opacity-0 group-hover:opacity-100 hover:text-foreground transition-all ${isMenuOpen ? "opacity-100" : ""
            }`}
        >
          <FaEllipsisH className="w-3 h-3" />
        </button>
        {isMenuOpen && (
          <div className="absolute right-0 top-full mt-1 w-36 border border-border rounded-lg shadow-lg z-50 overflow-hidden py-1 bg-popover">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(false);
                onShare && onShare();
              }}
              className="w-full flex items-center bg-transparent gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted text-left transition-colors"
            >
              <IoMdShareAlt className="w-4 h-4" /> Share
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(false);
                onDelete && onDelete();
              }}
              className="w-full flex items-center bg-transparent gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted text-left transition-colors"
            >
              <FaTrash className="w-3 h-3 text-red-600" /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


export default function ChatSidebar({ isReportPanelOpen, isExpanded, onToggle, onShareSession }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const appNavHistory = useContext(AppNavigationContext).appNavHistory;
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.app.datastate);

  const {
    useChatSessionList,
    useActiveChatSession,
    useCreateChatSession,
    useCreateChatProject,
    useActiveChatSessionId,
    useChatProjects,
    useActiveProjectId,
    useUpdateChatSession,
    useDeleteChatSession,
  } = useChatContext();

  const {
    data: chatSessions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingChatSessions,
  } = useChatSessionList();
  const { data: activeChatSession } = useActiveChatSession();
  const { mutate: createNewChatSession } = useCreateChatSession();
  const { mutateAsync: createNewProject } = useCreateChatProject();
  const [_, setActiveChatSessionId] = useActiveChatSessionId();
  const { data: projects } = useChatProjects();
  const [activeProjectId, setActiveProjectId] = useActiveProjectId();
  const { mutate: updateSession } = useUpdateChatSession();
  const { mutate: deleteSession } = useDeleteChatSession();

  // State
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isProfileMenuCompact, setIsProfileMenuCompact] = useState(false);
  const [isChatsExpanded, setIsChatsExpanded] = useState(true);
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  // Switch Accounts Logic
  const { profiles, selectedProfileId, switchAccount } = useSwitchAccounts();
  const selectedProfile = profiles?.find((profile) => profile.id === selectedProfileId);

  const userFullName = selectedProfile?.name || selectedProfile?.email || user?.email;

  const userDisplayName = selectedProfile?.name?.split(" ")[0] || selectedProfile?.email || user?.email || "My Profile";

  // Logout Logic
  function handleLogout() {
    showConfirm(
      t("menu.confirm-logout", "Are you sure you want to log out?"),
      t("menu.logout", "Logout"),
      "critical"
    )
      .then(() => {
        dispatch(setUser({}));
        removeCookie();
        appNavHistory.replace("/login");
      })
      .catch((err) => {
        Sentry.captureException(err, {
          extra: {
            context: 'ChatSidebar - user logout',
            hasUser: !!user,
            userEmail: user?.email
          }
        });
      });
  }

  const openProjectModal = () => {
    if (!user?._id) {
      showConfirm(
        "You need to sign in to create a new project.",
        "Authentication required",
        ""
      )
        .then(() => appNavHistory.replace("/login"))
        .catch(() => { });
      return;
    }
    setIsProjectModalOpen(true);
  };

  const handleCreateProject = async (title) => {
    await createNewProject({ title });
  };

  const handleRenameSession = (sessionId, currentTitle) => {
    const newTitle = prompt("Enter new chat title:", currentTitle);
    if (newTitle && newTitle !== currentTitle) {
      updateSession({ sessionId, sessionInfo: { title: newTitle } });
    }
  };

  const handleDeleteSession = (sessionId) => {
    showConfirm(
      "Are you sure you want to delete this chat?",
      "Delete Chat",
      "critical"
    ).then(() => {
      deleteSession({ sessionId });
    }).catch(() => { });
  };

  // Helper to close sidebar on mobile after selecting a chat
  const handleSelectChatSession = (sessionId) => {
    history.push(`/?id=${encodeURIComponent(sessionId)}`)
    // Close sidebar on mobile after selecting a chat
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      onToggle();
    }
  };

  if (!isExpanded) {
    // Collapsed narrow sidebar: show logo at top, icon buttons for chat/project/apps, and avatar at footer
    // Hidden on mobile - only visible on desktop
    return (
      <div className={`hidden md:flex flex-col h-full bg-background border-r border-border w-16 transition-all duration-300 items-center fixed top-0 left-0 bottom-0 z-50`}>
        <div className="py-4 flex flex-col items-center w-full space-y-4">
          <button
            onClick={onToggle}
            onMouseEnter={() => setIsLogoHovered(true)}
            onMouseLeave={() => setIsLogoHovered(false)}
            className="p-2 rounded-lg hover:bg-muted bg-transparent transition-colors focus:outline-none"
            title="Expand sidebar"
          >
            {isLogoHovered ? (
              <PanelLeftIcon className="h-5 w-5 text-muted-foreground" />
            ) : (
              <img src={logo} alt="MinoHealth" className="h-5 object-contain" />
            )}
          </button>

          <div className="flex flex-col items-center space-y-4 w-full px-2">
            <button
              onClick={() => {
                if (!user?._id) {
                  showConfirm("You need to sign in to start a new chat.", "Authentication required")
                    .then(() => appNavHistory.replace("/login"))
                    .catch(() => { });
                  return;
                }
                createNewChatSession();
                onToggle(); // Open sidebar
              }}
              className="p-2 w-full rounded-lg bg-transparent hover:bg-muted transition-colors focus:outline-none flex justify-center"
              title="New chat"
            >
              <BiEdit className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={onToggle}
              className="p-2 w-full rounded-lg bg-transparent hover:bg-muted transition-colors focus:outline-none flex justify-center"
              title="Apps"
            >
              <TbApps className="w-4 h-4 text-muted-foreground" />
            </button>

            <div className="w-8 border-t border-border"></div>

            <Link
              to="/anokye"
              onClick={onToggle}
              className="p-2 w-full rounded-lg bg-transparent hover:bg-muted transition-colors focus:outline-none flex justify-center"

            >
              <BsFolder className="w-4 h-4 text-muted-foreground" />
            </Link>
          </div>
        </div>

        <div className="flex-1" />

        <div className="py-4 relative">
          <ProfileMenuCustom
            isOpen={isProfileMenuOpen}
            onClose={() => {
              setIsProfileMenuOpen(false);
              setIsProfileMenuCompact(false);
            }}
            compact={isProfileMenuCompact}
            user={user}
            profiles={profiles}
            selectedProfile={selectedProfile}
            switchAccount={switchAccount}
            handleLogout={handleLogout}
            containerClassName="absolute bottom-full left-2 mb-3 z-50 text-xs"
          />

          <button
            onClick={() => {
              // If collapsed, expand sidebar then open compact profile menu
              if (!isExpanded) {
                onToggle();
                setIsProfileMenuCompact(true);
                // slight delay to allow parent to re-render expanded sidebar before opening menu
                setTimeout(() => setIsProfileMenuOpen(true), 50);
              } else {
                setIsProfileMenuOpen((s) => !s);
              }
            }}
            className="focus:outline-none hover:opacity-80 transition-opacity bg-transparent p-0"
            title="Profile"
          >
            <ProfileAvatar name={userFullName} size={32} className="rounded-full" />
          </button>
        </div>
      </div >
    );
  }

  return (
    <>
      {/* Mobile Overlay - only shown on mobile when sidebar is expanded */}
      {isExpanded && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onToggle}
        />
      )}

      <div className={`
        flex flex-col h-full bg-background border-r border-border
        transition-all duration-300

        /* Mobile: Fixed positioning with slide animation */
        fixed
        top-0 left-0 bottom-0
        z-50 md:z-auto
        w-64

        /* Mobile: Slide in/out animation */
        ${isExpanded ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}

        /* Desktop: Always visible with normal width */
        md:w-64
      `}>

        {/* Header Section */}
        <div className="px-1.5 pt-3 pb-2 flex-shrink-0">
          <div className="mb-4">
            <div className="flex items-center gap-2 px-1">
              <img src={logo} alt="MinoHealth" className="h-6 object-contain" />
              <span className="font-semibold text-sm tracking-tight" style={{ fontFamily: 'MuseoSans, sans-serif', color: 'var(--foreground)' }}>MinoHealth AI Labs</span>
            </div>
          </div>

          <NewChatButton onClick={createNewChatSession} />
        </div>

        {/* Static Content: Apps, Projects, Recents Header */}
        <div className="px-2 flex-shrink-0">
          {/* Apps Section */}
          <AppsList />

          {/* Projects Link */}
          <div className="mb-2">
            <Link
              to="/anokye"
              className="projects-link w-full flex items-center gap-2 px-2 py-2 text-sm text-foreground bg-transparent hover:bg-muted rounded-lg transition-colors group"
            >
              <FaRegFolder className="w-4 h-4 text-muted-foreground transition-all duration-200 group-hover:scale-110 group-hover:opacity-0 absolute" />
              <FaRegFolderOpen className="w-4 h-4 text-muted-foreground transition-all duration-200 opacity-0 group-hover:opacity-100 group-hover:scale-110" />
              <span className="font-medium">Projects</span>
            </Link>
          </div>

          {/* Collapsible Your chats header */}
          <div className="flex items-center justify-between px-2 py-1 mb-1">
            {activeProjectId ? (
              <button
                onClick={() => setActiveProjectId(null)}
                className="flex items-center bg-transparent gap-2 text-xs font-bold text-muted-foreground hover:text-foreground transition-colors"
              >
                <FaArrowLeft className="w-3 h-3" />
                <span className="uppercase tracking-wider truncate max-w-[150px]">
                  {projects?.find(p => p._id === activeProjectId)?.title || "Project"}
                </span>
              </button>
            ) : (
              <button
                onClick={() => setIsChatsExpanded(!isChatsExpanded)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center bg-transparent justify-between py-1 text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="tracking-wider">Recents</span>

                {isHovering && (
                  <span className="text-[11px] flex items-center gap-1">
                    {isChatsExpanded ? "Hide" : "Show"}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Content: Chat List */}
        <div className="flex-1 min-h-0 px-2">
          {isChatsExpanded && (
            <>
              {isLoadingChatSessions ? (
                <div className="space-y-1">
                  <div className="px-2 py-2 h-9 bg-muted rounded-lg animate-pulse" />
                  <div className="px-2 py-2 h-9 bg-muted rounded-lg animate-pulse" />
                  <div className="px-2 py-2 h-9 bg-muted rounded-lg animate-pulse" />
                </div>
              ) : chatSessions?.length > 0 ? (
                <Virtuoso
                  style={{ height: "100%" }}
                  className="custom-scrollbar"
                  data={chatSessions}
                  itemContent={(index, session) => (
                    <div className="pb-1">
                      <HistoryItem
                        label={session.title || session.topic || "New Chat"}
                        isActive={activeChatSession?._id === session._id}
                        isShared={session.user !== user?._id}
                        onClick={() => handleSelectChatSession(session._id)}
                        onShare={() => onShareSession && onShareSession(session)}
                        onDelete={() => handleDeleteSession(session._id)}
                      />
                    </div>
                  )}
                  endReached={() => {
                    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
                  }}
                  overscan={200}
                  components={{
                    Footer: () => isFetchingNextPage ? (
                      <div className="space-y-1 pt-1">
                        <div className="px-2 py-2 h-9 bg-muted rounded-lg animate-pulse" />
                        <div className="px-2 py-2 h-9 bg-muted rounded-lg animate-pulse" />
                      </div>
                    ) : null,
                  }}
                />
              ) : (
                <div className="px-3 py-6 text-center">
                  <p className="text-sm text-muted-foreground">No recent chats</p>
                  <p className="text-xs text-muted-foreground mt-1">Start a new conversation</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Section */}
        <div className="p-4 border-t border-border flex-shrink-0 relative">
          <ProfileMenuCustom
            isOpen={isProfileMenuOpen}
            onClose={() => setIsProfileMenuOpen(false)}
            user={user}
            profiles={profiles}
            selectedProfile={selectedProfile}
            switchAccount={switchAccount}
            handleLogout={handleLogout}
            containerClassName="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50"
          />

          <button
            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
            data-test="profile-menu-trigger-btn"
            className="profile-menu-trigger-btn flex items-center gap-2 w-full px-2 h-10 rounded-lg bg-transparent hover:text-foreground transition-colors group"
          >
            <div className="relative shrink-0">
              <ProfileAvatar name={userFullName} size={32} className="rounded-full border border-border" />
            </div>
            <span className="font-medium text-[16px] text-foreground truncate">
              {userDisplayName}
            </span>
          </button>
        </div>

      </div>

      {/* New Project Modal */}

      <NewProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        onCreateProject={handleCreateProject}
      />
    </>
  );
}
